import L from "leaflet";
import proj4 from "proj4";
// Define the projection transformations
const OSGB36_TO_WGS84 = proj4("EPSG:27700", "EPSG:4326");
const WGS84_TO_OSGB36 = proj4("EPSG:4326", "EPSG:27700");

export function ensureValidCoordinates(marker) {
  if (!marker) return null;
  const { correct } = marker;

  try {
    if (
      (typeof marker.lat === "string" && marker.lat === "--.------") ||
      (typeof marker.lng === "string" && marker.lng === "--.------")
    ) {
      return null;
    }

    const lat =
      typeof marker.lat === "number"
        ? marker.lat
        : parseFloat(String(marker.lat).trim());
    const lng =
      typeof marker.lng === "number"
        ? marker.lng
        : parseFloat(String(marker.lng).trim());

    if (isNaN(lat) || isNaN(lng)) {
      return null;
    }

    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return null;
    }

    return {
      lat,
      lng,
      alt: marker.alt ? marker.alt : 0,
      correct,
    };
  } catch (e) {
    console.error("Error processing marker:", marker, e);
    return null;
  }
}

export const formatOSGridRef = (x, y) => {
  // Convert coordinates to grid square letters
  const getGridSquare = (x, y) => {
    const majorX = Math.floor(x / 100000);
    const majorY = Math.floor(y / 100000);

    // First letter (500km squares)
    const firstLetters = ["S", "T", "N", "H", "O"];
    const firstLetter = firstLetters[majorY] || "T";

    // Second letter (100km squares)
    const secondLetters = [
      ["V", "W", "X", "Y", "Z"],
      ["Q", "R", "S", "T", "U"],
      ["L", "M", "N", "O", "P"],
      ["F", "G", "H", "J", "K"],
      ["A", "B", "C", "D", "E"],
    ];

    const secondLetter =
      (secondLetters[4 - majorY] && secondLetters[4 - majorY][majorX]) || "L";

    return firstLetter + secondLetter;
  };

  const gridSquare = getGridSquare(x, y);
  const localX = Math.floor(x % 100000);
  const localY = Math.floor(y % 100000);

  return `${gridSquare} ${localX.toString().padStart(5, "0")} ${localY
    .toString()
    .padStart(5, "0")}`;
};

// Modern pin design matching the screenshot
export const createModernPinIcon = (type = "default") => {
  const typeClass =
    type === "correct"
      ? "correct-modern-pin"
      : type === "incorrect"
      ? "incorrect-modern-pin"
      : type === "user"
      ? "user-modern-pin"
      : "modern-pin";

  return L.divIcon({
    html: `
      <div class="modern-pin ${typeClass}">
        <div class="pin-body">
          <div class="pin-dot"></div>
        </div>
      </div>
    `,
    className: "custom-pin-icon",
    iconSize: [32, 40],
    iconAnchor: [16, 40],
    popupAnchor: [0, -40],
  });
};

// Helper to validate standard lat/lng coordinates
export function isValidLatLngCoordinate(coord) {
  if (!coord) return false;

  const lat =
    typeof coord.lat === "number"
      ? coord.lat
      : typeof coord.lat === "string" && coord.lat !== "--.------"
      ? parseFloat(coord.lat)
      : null;

  const lng =
    typeof coord.lng === "number"
      ? coord.lng
      : typeof coord.lng === "string" && coord.lng !== "--.------"
      ? parseFloat(coord.lng)
      : null;

  return (
    lat !== null &&
    !isNaN(lat) &&
    lng !== null &&
    !isNaN(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

// Convert OS Grid coordinates to lat/lng
export function convertOSGridToLatLng(osGridCoord) {
  try {
    const x = osGridCoord.lat; // x coordinate stored in lat field
    const y = osGridCoord.lng; // y coordinate stored in lng field

    const [lng, lat] = OSGB36_TO_WGS84.forward([x, y]);

    return { lat, lng, originalX: x, originalY: y };
  } catch (error) {
    console.error("Error converting OS Grid to LatLng:", error);
    return null;
  }
}

// Helper to validate OS Grid coordinates (x, y format)
export function isValidOSGridCoordinate(coord) {
  if (!coord) return false;

  const x = typeof coord.lat === "number" ? coord.lat : null; // Using lat field for x coordinate
  const y = typeof coord.lng === "number" ? coord.lng : null; // Using lng field for y coordinate

  return (
    x !== null &&
    !isNaN(x) &&
    y !== null &&
    !isNaN(y) &&
    x >= 0 &&
    x <= 900000 && // OS Grid x range
    y >= 0 &&
    y <= 1400000 // OS Grid y range
  );
}

// Helper to calculate center from multiple markers
export const getCenterFromMarkers = (markers, gpsLogs) => {
  const defaultCenter = [51.5485, -2.5642];

  const validMarkers = markers.filter(isValidLatLngCoordinate);

  const validGpsCoordinates = gpsLogs
    .filter(
      (log) => log.coordinates && isValidOSGridCoordinate(log.coordinates)
    )
    .map((log) => convertOSGridToLatLng(log.coordinates))
    .filter(Boolean);

  const allValidCoords = [...validMarkers, ...validGpsCoordinates];

  if (allValidCoords.length === 0) return defaultCenter;

  const latSum = allValidCoords.reduce((sum, coord) => sum + coord.lat, 0);
  const lngSum = allValidCoords.reduce((sum, coord) => sum + coord.lng, 0);

  return [latSum / allValidCoords.length, lngSum / allValidCoords.length];
};

// Convert lat/lng to OS Grid coordinates
export function convertLatLngToOSGrid(latLng) {
  try {
    const [x, y] = WGS84_TO_OSGB36.forward([latLng.lng, latLng.lat]);
    return { x: Math.round(x), y: Math.round(y) };
  } catch (error) {
    console.error("Error converting LatLng to OS Grid:", error);
    return null;
  }
}
